import {
    AcceptDateBeforeOrAfter,
    BelopOperator,
    DateAsString,
    ForsikringDekningDTO,
    ForsikringStartDateWebDTO,
    SporsmalId,
    SporsmalType,
    TriggeredByCheckboxDTO,
    TriggeredByDatoDTO,
    TriggeredByDTOUnion,
    TriggeredByForsikringDTO,
    TriggeredByRadioDTO,
    TriggeredBySelectDTO,
    TriggeredByTallDTO,
    TriggeredByType,
    TriggeredByUtsattForgreiningDTO,
} from '../../types/generated-types';
import { ForsikringDekning } from '../../types/internal-types';
import { CurrentCheckboxSporsmal, isCheckboxSporsmal } from '../checkbox/checkbox-types';
import { isDatoSporsmal } from '../dato/dato-types';
import {
    CurrentForsikringsporsmal,
    isForsikringIngenAlternativerDataType,
    isForsikringListe,
    isForsikringSporsmal,
} from '../forsikring/forsikring-types';
import { CurrentRadiosporsmal, isRadioSporsmal } from '../radio/radio-types';
import { CurrentSelectSporsmal, isSelectSporsmal } from '../select/select-types';
import {
    CurrentSkadedatoAndForsikringSporsmal,
    isForsikringIngenAlternativerType,
    isForsikringListeType,
    isSkadedatoAndForsikringSporsmal,
} from '../skadedato-and-forsikring/skadedato-and-forsikring-types';
import { isCurrentSporsmalSide, toBesvartSporsmalSide } from '../sporsmalSide/sporsmalSide-types';
import { CurrentTallsporsmal, getBelopNumber, isTallSporsmal } from '../tall/tall-types';
import { CurrentSporsmal } from './current-steg';
import { Sporsmal } from './sporsmalsflyt-types';

export type TriggeredByRadio = TriggeredByRadioDTO;

export type TriggeredByCheckbox = TriggeredByCheckboxDTO;

export type TriggeredBySelect = TriggeredBySelectDTO;

export type TriggeredByForsikring = TriggeredByForsikringDTO;

export type TriggeredByTall = TriggeredByTallDTO;

export type TriggeredByDato = TriggeredByDatoDTO;

export type TriggeredByUtsattForgreining = TriggeredByUtsattForgreiningDTO;

export type TriggeredBy = TriggeredByDTOUnion;

const isTriggeredByRadio = (triggeredBy: TriggeredBy): triggeredBy is TriggeredByRadio =>
    triggeredBy.type === TriggeredByType.Radio;

const isTriggeredByCheckbox = (triggeredBy: TriggeredBy): triggeredBy is TriggeredByCheckbox =>
    triggeredBy.type === TriggeredByType.Checkbox;

const isTriggeredBySelect = (triggeredBy: TriggeredBy): triggeredBy is TriggeredBySelect =>
    triggeredBy.type === TriggeredByType.Select;

const isTriggeredByForsikring = (triggeredBy: TriggeredBy): triggeredBy is TriggeredByForsikring =>
    triggeredBy.type === TriggeredByType.Forsikring;

const isTriggeredByTall = (triggeredBy: TriggeredBy): triggeredBy is TriggeredByTall =>
    triggeredBy.type === TriggeredByType.Tall;

const isTriggeredByDato = (triggeredBy: TriggeredBy): triggeredBy is TriggeredByDato =>
    triggeredBy.type === TriggeredByType.Dato;

const isTriggeredByUtsattForgreining = (triggeredBy: TriggeredBy): triggeredBy is TriggeredByUtsattForgreining =>
    triggeredBy.type === TriggeredByType.UtsattForgreining;

const triggeredByRadioEquals = (triggeredBy1: TriggeredByRadio, triggeredBy2: TriggeredByRadio): boolean =>
    triggeredBy1.sporsmalId === triggeredBy2.sporsmalId && triggeredBy1.svarId === triggeredBy2.svarId;

const triggeredByCheckboxEquals = (triggeredBy1: TriggeredByCheckbox, triggeredBy2: TriggeredByCheckbox): boolean =>
    triggeredBy1.sporsmalId === triggeredBy2.sporsmalId && triggeredBy1.svarId === triggeredBy2.svarId;

const triggeredBySelectEquals = (triggeredBy1: TriggeredBySelect, triggeredBy2: TriggeredBySelect): boolean =>
    triggeredBy1.sporsmalId === triggeredBy2.sporsmalId && triggeredBy1.svarId === triggeredBy2.svarId;

const triggeredByForsikringEquals = (
    triggeredBy1: TriggeredByForsikring,
    triggeredBy2: TriggeredByForsikring,
): boolean =>
    triggeredBy1.sporsmalId === triggeredBy2.sporsmalId &&
    triggeredBy1.dekning === triggeredBy2.dekning &&
    triggeredBy1.forsikringStartDate === triggeredBy2.forsikringStartDate;

const triggeredByBelopEquals = (triggeredBy1: TriggeredByTall, triggeredBy2: TriggeredByTall): boolean =>
    triggeredBy1.sporsmalId === triggeredBy2.sporsmalId &&
    triggeredBy1.operator === triggeredBy2.operator &&
    triggeredBy1.belopLimit === triggeredBy2.belopLimit;

const triggeredByDatoEquals = (triggeredBy1: TriggeredByDato, triggeredBy2: TriggeredByDato): boolean =>
    triggeredBy1.sporsmalId === triggeredBy2.sporsmalId &&
    triggeredBy1.datoTrigger.dateToCompareWith === triggeredBy2.datoTrigger.dateToCompareWith &&
    triggeredBy1.datoTrigger.acceptDateBeforeOrAfter === triggeredBy2.datoTrigger.acceptDateBeforeOrAfter;

const triggeredByUtsattForgreiningEquals = (
    triggeredBy1: TriggeredByUtsattForgreining,
    triggeredBy2: TriggeredByUtsattForgreining,
): boolean => triggeredBy1.sporsmalId === triggeredBy2.sporsmalId;

export const triggeredByEquals = (triggeredBy1: TriggeredBy, triggeredBy2: TriggeredBy): boolean => {
    if (isTriggeredByRadio(triggeredBy1) && isTriggeredByRadio(triggeredBy2)) {
        return triggeredByRadioEquals(triggeredBy1, triggeredBy2);
    }
    if (isTriggeredByCheckbox(triggeredBy1) && isTriggeredByCheckbox(triggeredBy2)) {
        return triggeredByCheckboxEquals(triggeredBy1, triggeredBy2);
    }
    if (isTriggeredBySelect(triggeredBy1) && isTriggeredBySelect(triggeredBy2)) {
        return triggeredBySelectEquals(triggeredBy1, triggeredBy2);
    }
    if (isTriggeredByTall(triggeredBy1) && isTriggeredByTall(triggeredBy2)) {
        return triggeredByBelopEquals(triggeredBy1, triggeredBy2);
    }
    if (isTriggeredByDato(triggeredBy1) && isTriggeredByDato(triggeredBy2)) {
        return triggeredByDatoEquals(triggeredBy1, triggeredBy2);
    }
    if (isTriggeredByForsikring(triggeredBy1) && isTriggeredByForsikring(triggeredBy2)) {
        return triggeredByForsikringEquals(triggeredBy1, triggeredBy2);
    }
    if (isTriggeredByUtsattForgreining(triggeredBy1) && isTriggeredByUtsattForgreining(triggeredBy2)) {
        return triggeredByUtsattForgreiningEquals(triggeredBy1, triggeredBy2);
    }
    return false;
};

const isTriggeredByRadioSporsmal = (triggeredBy: TriggeredByRadio, currentSporsmal: CurrentRadiosporsmal): boolean =>
    triggeredBy.sporsmalId === currentSporsmal.sporsmal.id && triggeredBy.svarId === currentSporsmal.svarId;

const isTriggeredByCheckboxSporsmal = (
    triggeredBy: TriggeredByCheckbox,
    currentSporsmal: CurrentCheckboxSporsmal,
): boolean =>
    triggeredBy.sporsmalId === currentSporsmal.sporsmal.id &&
    currentSporsmal.alternativer.some((it) => it.checked && it.id === triggeredBy.svarId);

const isTriggeredBySelectSporsmal = (triggeredBy: TriggeredBySelect, currentSporsmal: CurrentSelectSporsmal): boolean =>
    triggeredBy.sporsmalId === currentSporsmal.sporsmal.id && triggeredBy.svarId === currentSporsmal.svarId;

const isTriggeredByStartDateOfForsikring = (
    startDateTrigger: ForsikringStartDateWebDTO,
    startDateOfForsikring?: DateAsString,
): boolean => {
    if (startDateTrigger === ForsikringStartDateWebDTO.NotApplicable) return true;
    const julyFirst2024 = new Date(2024, 6, 1);
    const januaryFirst2025: Date = new Date(2025, 0, 1);
    if (
        startDateTrigger === ForsikringStartDateWebDTO.BeforeJulyFirst2024 &&
        new Date(startDateOfForsikring) < julyFirst2024
    )
        return true;
    if (
        startDateTrigger === ForsikringStartDateWebDTO.BetweenJulyFirst2024ToJanuaryFirst2025 &&
        new Date(startDateOfForsikring) >= julyFirst2024 &&
        new Date(startDateOfForsikring) < januaryFirst2025
    )
        return true;
    if (
        startDateTrigger === ForsikringStartDateWebDTO.AfterJanFirst2025 &&
        new Date(startDateOfForsikring) >= januaryFirst2025
    )
        return true;
    return false;
};
const isTriggeredByForsikringSporsmal = (
    triggeredBy: TriggeredByForsikring,
    currentSporsmal: CurrentForsikringsporsmal,
): boolean => {
    const triggeredByCurrentSporsmal = triggeredBy.sporsmalId === currentSporsmal.sporsmal.id;
    const triggeredByForsikringListeAlternativ =
        isForsikringListe(currentSporsmal.data) && triggeredBy.dekning === currentSporsmal.data.selectedAlternativ?.id;
    const triggeredByForsikringIngenAlternativ =
        isForsikringIngenAlternativerDataType(currentSporsmal.data) &&
        triggeredBy.dekning === ForsikringDekningDTO.IngenValgt;
    const triggeredByStartDate =
        isForsikringListe(currentSporsmal.data) &&
        isTriggeredByStartDateOfForsikring(
            triggeredBy.forsikringStartDate,
            currentSporsmal.data.selectedAlternativ?.forsikringsperiode?.from,
        );

    return (
        triggeredByCurrentSporsmal &&
        ((triggeredByForsikringListeAlternativ && triggeredByStartDate) || triggeredByForsikringIngenAlternativ)
    );
};

const isForsikringlisteAndEqualDekning = (
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal,
    dekning: ForsikringDekning,
): boolean =>
    isForsikringListeType(currentSporsmal.forsikringData) &&
    dekning === currentSporsmal.forsikringData.selectedAlternativ?.id;

const isForsikringIngenAlternativerAndEqualDekning = (
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal,
    dekning: ForsikringDekning,
): boolean =>
    isForsikringIngenAlternativerType(currentSporsmal.forsikringData) && dekning === currentSporsmal.forsikringData.id;

const isTriggeredBySkadedatoAndForsikringSporsmal = (
    triggeredBy: TriggeredByForsikring,
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal,
): boolean =>
    triggeredBy.sporsmalId === currentSporsmal.sporsmal.id &&
    (isForsikringlisteAndEqualDekning(currentSporsmal, triggeredBy.dekning) ||
        isForsikringIngenAlternativerAndEqualDekning(currentSporsmal, triggeredBy.dekning));

const isTriggeredByTallSporsmal = (triggeredBy: TriggeredByTall, currentSporsmal: CurrentTallsporsmal): boolean => {
    if (triggeredBy.sporsmalId !== currentSporsmal.sporsmal.id) {
        return false;
    }
    if (triggeredBy.operator === BelopOperator.LessThan) {
        return getBelopNumber(currentSporsmal) < triggeredBy.belopLimit;
    }
    if (triggeredBy.operator === BelopOperator.GreaterThan) {
        return getBelopNumber(currentSporsmal) > triggeredBy.belopLimit;
    }
    return false;
};

const isTriggeredByDatoInSporsmal = (
    triggeredBy: TriggeredByDato,
    currentSporsmalId: SporsmalId,
    currentDato: DateAsString,
): boolean => {
    if (triggeredBy.sporsmalId !== currentSporsmalId) {
        return false;
    }
    if (triggeredBy.datoTrigger.acceptDateBeforeOrAfter === AcceptDateBeforeOrAfter.Before) {
        return new Date(triggeredBy.datoTrigger.dateToCompareWith) >= new Date(currentDato);
    }
    return new Date(triggeredBy.datoTrigger.dateToCompareWith) < new Date(currentDato);
};

export const isTriggeredBySporsmal = (triggeredBy: TriggeredBy, currentSporsmal: CurrentSporsmal): boolean => {
    if (isTriggeredByRadio(triggeredBy) && isRadioSporsmal(currentSporsmal)) {
        return isTriggeredByRadioSporsmal(triggeredBy, currentSporsmal);
    }
    if (isTriggeredByCheckbox(triggeredBy) && isCheckboxSporsmal(currentSporsmal)) {
        return isTriggeredByCheckboxSporsmal(triggeredBy, currentSporsmal);
    }
    if (isTriggeredBySelect(triggeredBy) && isSelectSporsmal(currentSporsmal)) {
        return isTriggeredBySelectSporsmal(triggeredBy, currentSporsmal);
    }
    if (isTriggeredByTall(triggeredBy) && isTallSporsmal(currentSporsmal)) {
        return isTriggeredByTallSporsmal(triggeredBy, currentSporsmal);
    }
    if (isTriggeredByDato(triggeredBy) && isDatoSporsmal(currentSporsmal)) {
        return isTriggeredByDatoInSporsmal(triggeredBy, currentSporsmal.sporsmal.id, currentSporsmal.dato);
    }
    if (isTriggeredByDato(triggeredBy) && isSkadedatoAndForsikringSporsmal(currentSporsmal)) {
        return isTriggeredByDatoInSporsmal(triggeredBy, currentSporsmal.sporsmal.id, currentSporsmal.skadedato);
    }
    if (isTriggeredByForsikring(triggeredBy) && isForsikringSporsmal(currentSporsmal)) {
        return isTriggeredByForsikringSporsmal(triggeredBy, currentSporsmal);
    }
    if (isTriggeredByForsikring(triggeredBy) && isSkadedatoAndForsikringSporsmal(currentSporsmal)) {
        return isTriggeredBySkadedatoAndForsikringSporsmal(triggeredBy, currentSporsmal);
    }
    if (isCurrentSporsmalSide(currentSporsmal)) {
        const besvartSporsmalSide = toBesvartSporsmalSide(currentSporsmal);
        return besvartSporsmalSide.besvarteSporsmal.some((besvartSporsmal) =>
            isTriggeredBySporsmal(triggeredBy, besvartSporsmal),
        );
    }
    return false;
};

export const canTriggerNewSporsmal = (sporsmal: Sporsmal) =>
    sporsmal.type === SporsmalType.Radio ||
    sporsmal.type === SporsmalType.Checkbox ||
    sporsmal.type === SporsmalType.Select ||
    sporsmal.type === SporsmalType.Forsikring ||
    sporsmal.type === SporsmalType.Tall ||
    sporsmal.type === SporsmalType.SkadedatoAndForsikring ||
    sporsmal.type === SporsmalType.Dato;
